import { CaretRightIcon, CheckCircleIcon, ClockIcon, TicketIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useGetVisitorParkingSession } from '@/api/visitor-parking/get-visitor-parking-session'
import { Button } from '@/components/business/visitor-parking/button'
import { DetailsCard } from '@/components/business/visitor-parking/details-card'
import { HelpSection } from '@/components/business/visitor-parking/help-section'
import { ParkingSessionError } from '@/components/business/visitor-parking/parking-session-error'
import { Icon } from '@/components/ui/icon'
import { VisitorParkingFormLayout } from '@/pages/visitor-parking/visitor-parking-form-layout'

import {
  VISITOR_PARKING_FREE_URL_KEY,
  VISITOR_PARKING_TICKET_NUMBER_URL_KEY,
  VISITOR_PARKING_TOKEN_URL_KEY,
} from '../visitor-parking.constants'
import { useInvalidateVisitorParkingSessionOnSSE } from '../visitor-parking.hooks'
import { formatParkingEndTime, getVisitorParkingSessionUrl } from '../visitor-parking.utils'
import * as S from './payment-success.styled'

export const PaymentSuccess = () => {
  const { t } = useTranslation(['visitor-parking'])
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get(VISITOR_PARKING_TOKEN_URL_KEY) ?? ''
  const ticketNumber = searchParams.get(VISITOR_PARKING_TICKET_NUMBER_URL_KEY) ?? ''
  const { data: parkingSession, isError, isLoading } = useGetVisitorParkingSession({ ticketNumber })
  useInvalidateVisitorParkingSessionOnSSE({
    sessionId: parkingSession?.sessionId,
    ticketNumber,
  })
  const isFree =
    searchParams.get(VISITOR_PARKING_FREE_URL_KEY) === 'true' || !parkingSession?.amount

  const handleTrackParking = () => {
    navigate(getVisitorParkingSessionUrl(ticketNumber, token, isFree))
  }

  return (
    <VisitorParkingFormLayout
      footer={
        <Button disabled={isError || isLoading} onClick={handleTrackParking}>
          {t('visitor-parking:paymentSuccess.trackButton')} <Icon icon={CaretRightIcon} />
        </Button>
      }
    >
      <S.Title>{t('visitor-parking:paymentSuccess.title')}</S.Title>
      {isError ? (
        <ParkingSessionError />
      ) : (
        <S.Card>
          <S.Icon icon={CheckCircleIcon} size="8rem" weight="fill" />
          <S.SuccessInfo>
            {isFree
              ? t('visitor-parking:paymentSuccess.freeInfo')
              : t('visitor-parking:paymentSuccess.paidInfo')}
          </S.SuccessInfo>
          <S.SuccessDescription>
            {isFree
              ? t('visitor-parking:paymentSuccess.freeDescription')
              : t('visitor-parking:paymentSuccess.paidDescription')}
          </S.SuccessDescription>
          <S.DetailsCardContainer>
            <DetailsCard
              icon={ClockIcon}
              isFree={isFree}
              // Note: for paid sessions, endDate is set after Stripe webhook — wait for it before showing.
              // For free sessions, endDate is set immediately on session creation.
              loading={isLoading || (!isFree && !parkingSession?.endDate)}
              title={t('visitor-parking:paymentSuccess.endTime')}
              value={formatParkingEndTime(parkingSession?.endDate)}
              variant="compact"
            />
            <S.Separator />
            <DetailsCard
              icon={TicketIcon}
              loading={isLoading}
              title={t('visitor-parking:paymentSuccess.ticketNumber')}
              value={parkingSession?.ticketNumber ?? ''}
              variant="compact"
            />
          </S.DetailsCardContainer>
        </S.Card>
      )}
      <HelpSection />
    </VisitorParkingFormLayout>
  )
}
